export interface DebouncedFunction<T extends (...args: never[]) => void> {
  (...args: Parameters<T>): Promise<ReturnType<T>>;
  cancel(): void;
  flush(): void;
  /**
   * True while a call is scheduled and has not yet committed.
   *
   * Callers that want to re-arm a debounced function without disturbing work already queued need
   * this: invoking the debounced function again would reset its timer and, under latest-wins,
   * replace the pending args (AVS-4399's dirty-key flush driver must do neither).
   */
  isPending(): boolean;
}

/** Rejection reason for debounced calls cancelled before their window committed. */
export class CancelledError extends Error {
  constructor(message = 'Debounced call cancelled') {
    super(message);
    this.name = 'CancelledError';
  }
}

/** Optional behavior hooks for {@link debounce}. */
export interface DebounceOptions<T extends (...args: never[]) => unknown> {
  /**
   * When a new call arrives while one is already pending, this computes the args the eventual
   * commit will use from the currently-pending args and the new call's args. Defaults to
   * latest-wins (the new call's args replace the pending ones) when omitted.
   */
  mergeArgs?: (pendingArgs: Parameters<T>, nextArgs: Parameters<T>) => Parameters<T>;
}

/**
 * Creates a debounced version of a function that delays its execution
 * until after `wait` milliseconds have elapsed since the last time it was invoked.
 *
 * All calls within one debounce window share a single promise that settles with
 * the final invocation's result. `cancel()` rejects a still-pending window with
 * `CancelledError`; once the timer fires the window is committed and cancel is a no-op.
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param options - Optional behavior hooks; see {@link DebounceOptions}. Omitting this parameter
 *   (or `mergeArgs`) keeps the default latest-wins behavior unchanged.
 * @returns A debounced version of the provided function
 */
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number,
  options?: DebounceOptions<T>
): DebouncedFunction<T> & { (): Promise<ReturnType<T>> } {
  interface PendingWindow {
    timeoutId: NodeJS.Timeout;
    args: Parameters<T>;
    promise: Promise<ReturnType<T>>;
    resolve: (value: ReturnType<T> | PromiseLike<ReturnType<T>>) => void;
    reject: (reason: unknown) => void;
  }

  let pending: PendingWindow | undefined;

  const commit = () => {
    if (!pending) {
      return;
    }
    const { timeoutId, args, resolve, reject } = pending;
    clearTimeout(timeoutId);
    // Clear refs synchronously at commit: cancel() now no-ops and the next call opens a fresh window.
    pending = undefined;
    try {
      resolve(func(...args) as ReturnType<T>);
    } catch (error) {
      reject(error);
    }
  };

  const debounced = function (...args: Parameters<T>): Promise<ReturnType<T>> {
    if (pending) {
      clearTimeout(pending.timeoutId);
      pending.args = options?.mergeArgs ? options.mergeArgs(pending.args, args) : args;
      pending.timeoutId = setTimeout(commit, wait);
      return pending.promise;
    }

    let resolve!: (value: ReturnType<T> | PromiseLike<ReturnType<T>>) => void;
    let reject!: (reason: unknown) => void;
    const promise = new Promise<ReturnType<T>>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    pending = {
      timeoutId: setTimeout(commit, wait),
      args,
      promise,
      resolve,
      reject,
    };
    return promise;
  } as DebouncedFunction<T> & { (): Promise<ReturnType<T>> };

  debounced.cancel = () => {
    if (!pending) {
      return;
    }
    const { timeoutId, promise, reject } = pending;
    clearTimeout(timeoutId);
    pending = undefined;
    // Pre-attach an internal handler so never-awaited callers surface no unhandled rejection.
    promise.catch(() => undefined);
    reject(new CancelledError('Debounced call cancelled'));
  };

  debounced.flush = commit;

  debounced.isPending = () => pending !== undefined;

  return debounced;
}

export function memoize<T extends (...args: never[]) => unknown>(fn: T): T {
  const cache = new Map<string, ReturnType<T>>();
  return function (...args: Parameters<T>): ReturnType<T> {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key) as ReturnType<T>;
    }
    const result = fn(...args) as ReturnType<T>;
    cache.set(key, result);
    return result;
  } as T;
}
